import React, { useRef, useEffect, useState } from "react";
import Doctor from "../../assets/img/new-home-page/LabcoatDoctor1.png";
import Ipad from "../../assets/img/new-home-page/iPadLandscape.png";
import EEG_Video from "../../assets/img/new-home-page/eeg-video.mp4";
import styles from "./ScrollSlideshow.module.css";

const slides = [
  {
    id: 1,
    badge: "Highly portable device",
    title: "Move to any location.",
    description:
      "CenSyn Pen EEG can be moved to any locations to collect EEG data anytime, anywhere.",
    type: "gif",
    media: EEG_Video,
    imageAlt: "Portable EEG device demonstration",
  },
  {
    id: 2,
    badge: "AI Enabled",
    title: "Seamless iOS app.",
    description:
      "CenSyn Pen EEG can stream real time EEG data for screening inside or outside hospital walls.",
    type: "image",
    media: Ipad,
    imageAlt: "iPad showing EEG data interface",
  },
  {
    id: 3,
    badge: "Highly portable device",
    title: "Carry it all day.",
    description:
      "The device can move with you throughout the day to collect data on the go.",
    type: "image",
    media: Doctor,
    imageAlt: "Doctor wearing EEG device in lab coat",
  },
];

const ScrollSlideshow = () => {
  const slideRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(
    Array(slides.length).fill(false)
  );
  const [mediaActive, setMediaActive] = useState(
    Array(slides.length).fill(false)
  );

  useEffect(() => {
    const handleScroll = () => {
      const offsets = slideRefs.current.map(
        (ref) => ref?.getBoundingClientRect().top || 0
      );
      const index = offsets.findIndex((top) => top > window.innerHeight * 0.2);
      setActiveIndex(index === -1 ? slides.length - 1 : Math.max(0, index - 1));

      // Animation trigger
      setVisibleSlides((prev) =>
        slideRefs.current.map((ref, idx) => {
          if (!ref) return false;
          const rect = ref.getBoundingClientRect();
          return (
            rect.top < window.innerHeight * 0.7 &&
            rect.bottom > window.innerHeight * 0.2
          );
        })
      );

      // Media animation trigger
      setMediaActive((prev) =>
        slideRefs.current.map((ref, idx) => {
          if (!ref) return false;
          const rect = ref.getBoundingClientRect();
          // Media active when the section is more centered in the viewport
          return (
            rect.top < window.innerHeight * 0.5 &&
            rect.bottom > window.innerHeight * 0.5
          );
        })
      );
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="tw-flex tw-w-full tw-relative tw-flex-row">
      <div className="tw-flex-1">
        {slides.map((slide, idx) => (
          <section
            key={slide.id}
            ref={(el) => (slideRefs.current[idx] = el)}
            className="sm:tw-min-h-[50vh] md:tw-min-h-[100vh] tw-flex tw-items-center tw-justify-center"
          >
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-w-[90%] md:tw-w-4/5 tw-mx-auto">
              <div className="tw-flex-1 tw-flex tw-justify-center tw-mb-6 md:tw-mb-[100px]">
                <div
                  className={`tw-bg-transparent tw-flex-1 tw-flex tw-justify-center ${
                    styles["media-anim"]
                  }${
                    mediaActive[idx] ? " " + styles["media-anim--visible"] : ""
                  }`}
                >
                  {slide.type === "image" ? (
                    <img
                      src={slide.media}
                      alt={slide.imageAlt}
                      className="mt-2 tw-max-w-full md:tw-max-w-[180%] tw-max-h-[250px] sm:tw-max-h-[550px] md:tw-max-h-[650px] tw-rounded-lg tw-mr-[0%] sm:tw-mr-[100%] md:tw-mr-[78%] "
                      style={{ border: "none", background: "transparent" }}
                    />
                  ) : (
                    <video
                      src={EEG_Video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="tw-w-[290px] tw-h-[300px]  sm:tw-w-[350px] sm:tw-h-[400px] md:tw-w-[350px] md:tw-h-[450px] md:tw-mr-[20%] lg:tw-w-[650px] lg:tw-h-[600px] 
 tw-object-cover tw-m-0 tw-p-0 tw-border-none tw-shadow-none tw-bg-transparent tw-block"
                    />
                  )}
                </div>
              </div>
              <div className="tw-flex-1 md:tw-pl-10">
                <div
                  className={`${styles["text-anim"]}${
                    visibleSlides[idx] ? " " + styles["text-anim--visible"] : ""
                  }`}
                  style={{
                    transitionDelay: visibleSlides[idx] ? "0.1s" : "0s",
                  }}
                >
                  <div className="tw-text-[#4B7B2B] tw-font-medium tw-mb-2 tw-text-sm md:tw-text-base">
                    {slide.badge}
                  </div>
                  <h2 className="tw-text-[22px] md:tw-text-[28px] tw-font-semibold tw-mb-4 tw-text-center md:tw-text-left">
                    {slide.title}
                  </h2>
                  <div className="tw-text-[#222] tw-text-sm md:tw-text-base tw-text-center md:tw-text-left">
                    {slide.description}
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      <div className="tw-hidden md:tw-flex tw-w-6 tw-flex-col tw-items-center tw-justify-center tw-sticky tw-top-[100px] tw-h-[60vh] tw-mr-8">
        {slides.map((_, idx) => (
          <div
            key={idx}
            className={`tw-w-[6px] tw-h-[60px] tw-rounded tw-my-3 ${
              idx === activeIndex ? "tw-bg-[#7FFFD4]" : "tw-bg-[#ccc]"
            } tw-transition-colors tw-duration-300`}
          />
        ))}
      </div>
    </div>
  );
};

export default ScrollSlideshow;
